import { X, Upload, ChevronDown, Check, Info, Wrench } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const conditionOptions = [
  { value: "New", label: "Brand New / Pristine" },
  { value: "Good", label: "Good / Operational" },
  { value: "Fair", label: "Fair / Signs of Wear" },
  { value: "Poor", label: "Poor / Requires Attention" },
];

export default function RoomEquipmentModal({
  isOpen,
  onClose,
  onSubmit,
  formData,
  setFormData,
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentCondition = conditionOptions.find(opt => opt.value === formData.condition);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 overflow-y-auto">
          {/* Ambient Backdrop Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-slate-950/40 backdrop-blur-[6px]"
          />

          {/* Premium Form Container Card */}
          <motion.div
            initial={{ scale: 0.98, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.98, opacity: 0, y: 8 }}
            transition={{ type: "spring", duration: 0.3, bounce: 0 }}
            className="bg-white rounded-xl w-full max-w-lg shadow-2xl border border-slate-100 relative z-10 my-auto flex flex-col overflow-hidden"
          >
            <div className="absolute top-0 left-6 right-6 h-[2px] bg-gradient-to-r from-transparent via-[#002855]/30 to-transparent" />
            {/* Modal Header Module */}
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <div>
                <h2 className="text-sm font-semibold text-slate-900 tracking-tight flex items-center gap-2">
                  <Wrench size={15} className="text-[#002855]" />
                  {formData.id ? "Modify Asset Parameters" : "Register New Equipment Asset"}
                </h2>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Ensure telemetry attributes synchronize accurately with database metrics.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200/50 transition-colors cursor-pointer"
              >
                <X size={15} />
              </button>
            </div>

            {/* Modal Body / Form Workspace */}
            <form onSubmit={onSubmit} className="p-6 space-y-4 text-xs font-medium text-slate-600">
              
              {/* Equipment Title Field */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  Equipment Title <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g., Logitech C920 Webcam, Dell P2422H"
                  value={formData.name || ""}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50/50 border border-slate-200 rounded-xl font-medium text-slate-800 placeholder-slate-400 outline-none focus:ring-2 focus:ring-[#002855]/10 focus:border-[#002855] focus:bg-white transition-all"
                  required
                />
              </div>

              {/* Responsive Condition & Quantity Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                
                {/* Physical Condition Dropdown Container */}
                <div className="space-y-1.5 relative" ref={dropdownRef}>
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                    Physical Condition <span className="text-rose-500">*</span>
                  </label>
                  
                  <button
                    type="button"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="w-full px-3 py-2 bg-slate-50/50 border border-slate-200 rounded-xl font-medium text-slate-800 flex items-center justify-between outline-none focus:ring-2 focus:ring-[#002855]/10 focus:border-[#002855] hover:bg-white transition-all cursor-pointer shadow-xs"
                  >
                    <span className={currentCondition ? "text-slate-800" : "text-slate-400"}>
                      {currentCondition ? currentCondition.label : "Select Condition"}
                    </span>
                    <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${dropdownOpen ? "rotate-180 text-slate-600" : ""}`} />
                  </button>

                  {/* Dropdown Box Overlay */}
                  <AnimatePresence>
                    {dropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.15 }}
                        className="absolute left-0 right-0 mt-1.5 bg-white border border-slate-200 shadow-xl rounded-xl p-1 z-50"
                      >
                        {conditionOptions.map((opt) => {
                          const isSelected = formData.condition === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => {
                                setFormData({ ...formData, condition: opt.value });
                                setDropdownOpen(false);
                              }}
                              className={`w-full flex items-center justify-between px-2.5 py-2 text-left rounded-lg font-medium transition-all cursor-pointer ${
                                isSelected 
                                  ? "bg-[#002855]/5 text-[#002855] font-semibold" 
                                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                              }`}
                            >
                              <span>{opt.label}</span>
                              {isSelected && <Check size={12} className="text-[#002855] stroke-[2.5px]" />}
                            </button>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Unit Quantity Field */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                    Unit Quantity <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="number"
                    placeholder="0"
                    min="1"
                    value={formData.quantity || ""}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50/50 border border-slate-200 rounded-xl font-medium text-slate-800 placeholder-slate-400 outline-none focus:ring-2 focus:ring-[#002855]/10 focus:border-[#002855] focus:bg-white transition-all"
                    required
                  />
                </div>
              </div>

              {/* Supplemental Documentation Notes */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  Supplemental Documentation / Notes
                </label>
                <textarea
                  placeholder="Describe internal specifications, tracking numbers, or asset assignments..."
                  value={formData.description || ""}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 bg-slate-50/50 border border-slate-200 rounded-xl font-medium text-slate-800 placeholder-slate-400 outline-none focus:ring-2 focus:ring-[#002855]/10 focus:border-[#002855] focus:bg-white transition-all resize-none min-h-[70px]"
                />
              </div>

              {/* Photographic Verification Dropzone Frame */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  Asset Photographic Verification
                </label>
                <div className="relative group border border-dashed border-slate-200 hover:border-[#002855]/40 bg-slate-50/30 rounded-xl p-5 transition-colors flex flex-col items-center justify-center text-center">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFormData({ ...formData, picture: e.target.files[0] })}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  />
                  <div className="p-2 bg-white border border-slate-100 rounded-xl shadow-xs text-slate-400 group-hover:text-[#002855] group-hover:border-[#002855]/20 transition-colors mb-2">
                    <Upload size={14} />
                  </div>
                  <p className="text-[11px] font-semibold text-slate-700">
                    {formData.picture ? formData.picture.name : "Click to select hardware imagery"}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    {formData.picture ? `${(formData.picture.size / 1024 / 1024).toFixed(2)} MB` : "Accepts standard JPG, PNG matrices"}
                  </p>
                </div>
              </div>

              {/* Actions Footer Section Controls */}
              <div className="flex items-center justify-between pt-3 border-t border-slate-100 mt-6">
                <div className="hidden sm:flex items-center gap-1.5 text-[10px] font-medium text-slate-400">
                  <Info size={12} className="text-slate-300 shrink-0" />
                  Fields marked with (<span className="text-rose-500">*</span>) required.
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 bg-white border border-slate-200 rounded-xl transition-all cursor-pointer"
                  >
                    Discard
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-xs font-medium text-white bg-[#002855] bg-opacity-80 hover:bg-opacity-95 rounded-xl shadow-xs transition-all cursor-pointer active:scale-98"
                  >
                    {formData.id ? "Commit Updates" : "Save Asset"}
                  </button>
                </div>
              </div>

            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}